import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getMembershipState } from '../utils/membershipState';
import './UserMenu.css';

const MEMBERSHIP_META = {
    general:   { label: 'General Member',  color: '#1AC8DB', icon: '🌍', short: 'General'  },
    active:    { label: 'Active Member',   color: '#3B82F6', icon: '⭐', short: 'Active'   },
    volunteer: { label: 'Volunteer',       color: '#10B981', icon: '🤝', short: 'Volunteer' },
    advisor:   { label: 'Advisor',         color: '#F59E0B', icon: '🎓', short: 'Advisor'  },
};

const getInitials = (name = '') => {
    const parts = name.trim().split(/\s+/);
    if (parts.length === 0 || !parts[0]) return '?';
    if (parts.length === 1) return parts[0][0].toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const UserMenu = () => {
    const { user, userProfile, logout } = useAuth();
    const [open, setOpen] = useState(false);
    const [loggingOut, setLoggingOut] = useState(false);
    const menuRef = useRef(null);

    const name        = userProfile?.name || user?.displayName || 'Member';
    const membershipState = getMembershipState(userProfile);
    const memberType  = membershipState.displayMembershipType || 'general';
    const meta        = MEMBERSHIP_META[memberType] || MEMBERSHIP_META.general;
    const initials    = getInitials(name);
    const firstName   = name.split(' ')[0];
    const badgeLabel = membershipState.isAdvisorUnderReview
        ? 'Review'
        : membershipState.canRetryPayment
            ? 'Pending'
            : meta.short;

    // Close on outside click
    useEffect(() => {
        const handler = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    // Close on Escape
    useEffect(() => {
        const handler = (e) => { if (e.key === 'Escape') setOpen(false); };
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, []);

    const handleLogout = async () => {
        setLoggingOut(true);
        try {
            await logout();
        } finally {
            setLoggingOut(false);
            setOpen(false);
        }
    };

    return (
        <div className="um-wrap" ref={menuRef}>
            {/* Trigger button */}
            <button
                className={`um-trigger ${open ? 'um-trigger--open' : ''}`}
                onClick={() => setOpen(v => !v)}
                aria-haspopup="true"
                aria-expanded={open}
                aria-label="Account menu"
            >
                {/* Avatar */}
                <span
                    className="um-avatar"
                    style={{ background: `linear-gradient(135deg, ${meta.color}55, ${meta.color}22)`, borderColor: `${meta.color}50` }}
                >
                    {initials}
                </span>

                {/* Name + badge */}
                <span className="um-info">
                    <span className="um-name">{firstName}</span>
                    <span
                        className="um-badge"
                        style={{ color: meta.color, background: `${meta.color}18`, borderColor: `${meta.color}30` }}
                    >
                        {meta.icon} {badgeLabel}
                    </span>
                </span>

                {/* Chevron */}
                <span className={`um-chevron ${open ? 'um-chevron--up' : ''}`}>
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                        <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.6"
                              strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                </span>
            </button>

            {/* Dropdown */}
            {open && (
                <div className="um-dropdown" role="menu">
                    {/* Profile header */}
                    <div className="um-dropdown-header">
                        <span
                            className="um-dropdown-avatar"
                            style={{ background: `linear-gradient(135deg, ${meta.color}55, ${meta.color}22)`, borderColor: `${meta.color}40` }}
                        >
                            {initials}
                        </span>
                        <div className="um-dropdown-info">
                            <span className="um-dropdown-name">{name}</span>
                            <span className="um-dropdown-email">{userProfile?.email || user?.email || ''}</span>
                        </div>
                    </div>

                    {/* Membership card */}
                    <div
                        className="um-membership-card"
                        style={{ background: `${meta.color}0e`, borderColor: `${meta.color}25` }}
                    >
                        <span className="um-membership-icon">{meta.icon}</span>
                        <div className="um-membership-details">
                            <span className="um-membership-type" style={{ color: meta.color }}>
                                {meta.label}
                            </span>
                            <span className="um-membership-status">
                                <span className="um-status-dot" />
                                {membershipState.isAdvisorUnderReview ? 'Under Review' : membershipState.isActivated ? 'Active' : membershipState.canRetryPayment ? 'Payment Pending' : 'Active'}
                            </span>
                        </div>
                    </div>

                    {/* Menu items */}
                    <div className="um-divider" />

                    <div className="um-menu-items">
                        <Link to="/profile" className="um-menu-item" role="menuitem" onClick={() => setOpen(false)}>
                            <span className="um-menu-item-icon">
                                <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
                                    <circle cx="7.5" cy="5" r="3" stroke="currentColor" strokeWidth="1.3"/>
                                    <path d="M2 13c0-3 2.5-5 5.5-5s5.5 2 5.5 5"
                                          stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
                                </svg>
                            </span>
                            My Profile
                        </Link>

                        <Link to="/donate" className="um-menu-item" role="menuitem" onClick={() => setOpen(false)}>
                            <span className="um-menu-item-icon">
                                <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
                                    <path d="M7.5 12.5C7.5 12.5 1.5 8.5 1.5 4.5C1.5 2.84 2.84 1.5 4.5 1.5C5.6 1.5 6.56 2.06 7.5 3C8.44 2.06 9.4 1.5 10.5 1.5C12.16 1.5 13.5 2.84 13.5 4.5C13.5 8.5 7.5 12.5 7.5 12.5Z"
                                          stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </span>
                            Donate
                        </Link>
                    </div>

                    <div className="um-divider" />

                    {/* Logout */}
                    <button
                        className="um-menu-item um-menu-item--logout"
                        role="menuitem"
                        onClick={handleLogout}
                        disabled={loggingOut}
                    >
                        <span className="um-menu-item-icon">
                            {loggingOut ? (
                                <span className="um-logout-spinner" />
                            ) : (
                                <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
                                    <path d="M10 5l3 2.5L10 10"
                                          stroke="currentColor" strokeWidth="1.3"
                                          strokeLinecap="round" strokeLinejoin="round"/>
                                    <path d="M13 7.5H6"
                                          stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
                                    <path d="M6 2.5H3a1 1 0 00-1 1v8a1 1 0 001 1h3"
                                          stroke="currentColor" strokeWidth="1.3"
                                          strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            )}
                        </span>
                        {loggingOut ? 'Signing out…' : 'Sign Out'}
                    </button>
                </div>
            )}
        </div>
    );
};

export default UserMenu;
